// src/feature-module/admin/shadowing/AdminShadowingSubmissionsListPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaMicrophone,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Submission = {
  id: number;
  studentId: number;
  studentName: string;
  audioDownloadUrl: string;
  submittedAt: string;
  reviewed: boolean;
  mentorFeedback: string;
};

type Task = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
};

const AdminShadowingSubmissionsListPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<Task | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchData();
  }, [taskId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch task details
      const taskResponse = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}`,
        { credentials: "include" }
      );
      if (taskResponse.ok) {
        const taskData = await taskResponse.json();
        setTask(taskData);
      }

      // Fetch submissions
      const submissionsResponse = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}/submissions`,
        { credentials: "include" }
      );
      if (submissionsResponse.ok) {
        const submissionsData = await submissionsResponse.json();
        setSubmissions(submissionsData);
      } else {
        setError("Failed to load submissions");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaMicrophone />}
        title="Shadowing Task Submissions"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Shadowing Tasks", to: "/admin/shadowing-tasks" },
          { label: "Submissions" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Task Info */}
        {task && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Task Details
            </h2>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-800 font-bold rounded-lg">
                {task.taskNumber}
              </span>
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                  {task.level}
                </span>
                <p className="text-gray-700 mt-1">{task.taskDescription}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submissions List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
            <h3 className="text-xl font-bold">
              Student Submissions ({submissions.length})
            </h3>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
                <p className="text-gray-600">Loading submissions...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <FaTimesCircle className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">
                  No Submissions Yet
                </h4>
                <p className="text-gray-500 text-sm">
                  No students have submitted this task yet.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Student
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Submitted At
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Audio
                      </th>
                      <th className="py-3 px-4 text-center text-sm font-semibold text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission, index) => (
                      <tr
                        key={submission.id}
                        className={`border-b border-gray-100 ${
                          index % 2 === 1 ? "bg-gray-50" : "bg-white"
                        } hover:bg-blue-50 transition-colors`}
                      >
                        <td className="py-4 px-4">
                          <p className="font-semibold text-gray-900">
                            {submission.studentName}
                          </p>
                          <p className="text-sm text-gray-500">
                            ID: {submission.studentId}
                          </p>
                        </td>
                        <td className="py-4 px-4 text-gray-600 text-sm">
                          {new Date(submission.submittedAt).toLocaleString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {submission.reviewed ? (
                            <span className="inline-flex items-center gap-1.5 text-green-700 bg-green-50 px-3 py-1 rounded-full text-xs font-semibold border border-green-200">
                              <FaCheckCircle className="text-green-500" />
                              Reviewed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-yellow-700 bg-yellow-50 px-3 py-1 rounded-full text-xs font-semibold border border-yellow-200">
                              <FaTimesCircle className="text-yellow-500" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <audio
                            controls
                            className="w-48"
                            src={submission.audioDownloadUrl}
                          >
                            Your browser does not support audio playback.
                          </audio>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/shadowing-tasks/submissions/${submission.id}/review`
                              )
                            }
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-colors shadow-sm"
                          >
                            <FaEye />
                            {submission.reviewed ? "View Review" : "Review"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6">
          <button
            onClick={() => navigate("/admin/shadowing-tasks")}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            ← Back to Shadowing Tasks
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminShadowingSubmissionsListPage;
